import { type BadgeConfig } from "@pimo/pimo-components";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import dayjs from "dayjs";
import {
  BffReport,
  COLORS,
  CONTRAST_TEXT_COLORS,
  ReportingDetails,
} from "product-test-types";
import { getReportingStatus } from "product-test-utils";

import { formatDate, formatDateTime } from "./format-date";

export function getProjectStatusBadge({
  currentReport,
  language,
  reportingCycle,
}: {
  currentReport?: BffReport;
  lastUpdatedAt?: string;
  reportingCycle?: ReportingDetails;
  language: string;
}): BadgeConfig {
  const completed = dayjs(
    currentReport?.project?._calculated?.completionDate
  ).isValid();
  const submittedAt = currentReport?.project?.submittedAt;
  const submitted = Boolean(currentReport?.project?.submitter);

  const lastUpdatedAt = currentReport?.project?._calculated?.lastUpdatedAt;
  const lastUpdatedBy = currentReport?.project?._calculated?.lastUpdatedBy;
  const updateStatus =
    reportingCycle && reportingCycle.frequency && reportingCycle.unit
      ? getReportingStatus({
          endReportingDateInStringFormat: reportingCycle?.endReportingCycleDate,
          frequency: reportingCycle?.frequency,
          unit: reportingCycle?.unit,
          lastUpdatedDateInStringFormat:
            currentReport?.project?._calculated?.lastUpdatedAt,
        })
      : {
          color: COLORS[0],
          muiIcon: "SentimentSatisfied",
          status: getLocalizedMessage(language, "updated") || "Updated",
          icon: "mood-smile.svg",
          contrastTextColor: CONTRAST_TEXT_COLORS[0],
        };

  const updateStatusBadge: BadgeConfig = {
    hoverText: updateStatus?.status
      ? `${updateStatus?.status} ${
          lastUpdatedAt
            ? `${
                getLocalizedMessage(language, "asOf") || "as of"
              } ${formatDateTime(lastUpdatedAt, language)}`
            : ""
        }${
          lastUpdatedBy
            ? ` ${getLocalizedMessage(language, "by") || "by"} ${lastUpdatedBy}`
            : ""
        }`
      : "N/A",
    label: submitted
      ? `Submitted: ${
          submittedAt && dayjs(submittedAt).isValid()
            ? formatDate(submittedAt, language)
            : "N/A"
        }`
      : completed
        ? `${getLocalizedMessage(language, "completedOn") || "Completed On"} ${
            currentReport?.project?._calculated?.completionDate
              ? formatDate(
                  currentReport.project._calculated.completionDate,
                  language
                )
              : "N/A"
          }`
        : `${
            getLocalizedMessage(language, "nextReportingBy") ||
            "Next Reporting by"
          } ${
            reportingCycle?.endReportingCycleDate
              ? formatDate(reportingCycle.endReportingCycleDate, language)
              : "N/A"
          }`,
    icon: !completed && !submitted ? updateStatus.icon : undefined,
    muiIcon: completed || submitted ? "CheckCircleOutlined" : undefined,
    iconColor:
      completed || submitted
        ? CONTRAST_TEXT_COLORS[0]
        : updateStatus?.contrastTextColor,
    backgroundColor: completed || submitted ? COLORS[0] : updateStatus?.color,
    color:
      completed || submitted
        ? CONTRAST_TEXT_COLORS[0]
        : updateStatus?.contrastTextColor,
  };

  return updateStatusBadge;
}
